import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import CommercialHeader from "@/components/commercial/CommercialHeader";
import { api, fmtEur } from "@/lib/commercialApi";
import { COMMERCIAL_STAGES, stageById } from "@/lib/commercialStages";
import { SIMPLE_BUCKETS, bucketFor } from "@/lib/commercialUx";
import { Loader2, Phone, LayoutGrid, Rows3, Search } from "lucide-react";

export default function ComOpportunities() {
  const [opps, setOpps] = useState(null);
  const [filter, setFilter] = useState({ teleprospectrice: "", commercial: "", q: "" });
  const [meta, setMeta] = useState({ teleprospectrices: [], commerciaux: [] });
  const [view, setView] = useState(() => localStorage.getItem("com_view") || "simple");

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.teleprospectrice, filter.commercial]);

  useEffect(() => { localStorage.setItem("com_view", view); }, [view]);

  async function load() {
    setOpps(null);
    const params = {};
    if (filter.teleprospectrice) params.teleprospectrice = filter.teleprospectrice;
    if (filter.commercial) params.commercial = filter.commercial;
    const r = await api.get("/opportunities", { params });
    setOpps(r.data.opportunities);
  }

  async function dropTo(oppId, newStage) {
    try {
      await api.patch(`/opportunities/${oppId}`, { stage: newStage });
      setOpps((cur) => cur.map((o) => (o.id === oppId ? { ...o, stage: newStage } : o)));
      toast.success(`Déplacée vers ${stageById(newStage).label}`);
    } catch { toast.error("Erreur de mise à jour"); }
  }

  const q = filter.q.trim().toLowerCase();
  const filtered = (opps || []).filter((o) => !q || (o.entreprise_name || "").toLowerCase().includes(q) || (o.contact_nom || "").toLowerCase().includes(q));

  return (
    <div className="min-h-screen bg-brand-paper">
      <CommercialHeader />
      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6 lg:py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
          <div>
            <div className="eyebrow mb-1">Pipeline commercial</div>
            <h1 className="font-display text-3xl text-brand-ink">Opportunités</h1>
            <p className="mt-1 text-sm text-brand-subtle">
              {view === "simple"
                ? "Vue simplifiée en 5 étapes pour avancer rapidement. Bascule en vue complète pour les 10 étapes détaillées."
                : "Vue complète avec les 10 étapes du pipeline. Glisse-dépose une opportunité d'une colonne à l'autre."}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <div className="inline-flex items-center rounded-md border border-brand-line bg-white p-0.5">
              <button
                onClick={() => setView("simple")}
                data-testid="view-simple"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] ${view === "simple" ? "bg-brand-ink text-white" : "text-brand-ink/70 hover:bg-brand-paper"}`}
              ><Rows3 className="h-3 w-3" />Simplifié</button>
              <button
                onClick={() => setView("full")}
                data-testid="view-full"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] ${view === "full" ? "bg-brand-ink text-white" : "text-brand-ink/70 hover:bg-brand-paper"}`}
              ><LayoutGrid className="h-3 w-3" />Complet (10 étapes)</button>
            </div>
            <div className="relative">
              <Search className="h-3 w-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-subtle" />
              <input
                placeholder="Rechercher…" value={filter.q}
                onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
                data-testid="opps-search"
                className="h-9 pl-7 pr-3 text-xs rounded-md border border-brand-line bg-white w-44"
              />
            </div>
            <select value={filter.teleprospectrice} onChange={(e) => setFilter((f) => ({ ...f, teleprospectrice: e.target.value }))} data-testid="filter-telepro" className="h-9 px-3 text-xs rounded-md border border-brand-line bg-white">
              <option value="">Toutes téléprospectrices</option>
              {meta.teleprospectrices.map((t) => <option key={t}>{t}</option>)}
            </select>
            <select value={filter.commercial} onChange={(e) => setFilter((f) => ({ ...f, commercial: e.target.value }))} data-testid="filter-commercial" className="h-9 px-3 text-xs rounded-md border border-brand-line bg-white">
              <option value="">Tous commerciaux</option>
              {meta.commerciaux.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {opps === null ? (
          <div className="flex items-center gap-2 text-brand-subtle"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
        ) : filtered.length === 0 ? (
          <EmptyOpps />
        ) : view === "simple" ? (
          <SimpleBoard opps={filtered} />
        ) : (
          <FullBoard opps={filtered} dropTo={dropTo} />
        )}
      </div>
    </div>
  );
}

function EmptyOpps() {
  return (
    <div className="rounded-lg border border-brand-line bg-white p-10 text-center max-w-xl mx-auto">
      <div className="font-display text-xl text-brand-ink">Pas encore d'opportunité</div>
      <p className="mt-2 text-sm text-brand-subtle">
        Les opportunités sont créées automatiquement dès qu'un prospect passe en « Intéressé » ou « RDV pris » dans Prospection.
        Tu peux aussi utiliser le bouton « Convertir en opportunité » sur une fiche prospect.
      </p>
      <Link to="/prospection" className="mt-5 inline-flex items-center gap-2 h-9 px-4 rounded-md bg-brand-ink text-white text-xs hover:bg-brand-moss">
        Aller dans Prospection
      </Link>
    </div>
  );
}

function SimpleBoard({ opps }) {
  const byBucket = {};
  SIMPLE_BUCKETS.forEach((b) => { byBucket[b.id] = []; });
  opps.forEach((o) => {
    const b = bucketFor(o);
    if (b) byBucket[b.id].push(o);
  });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="simple-board">
      {SIMPLE_BUCKETS.map((b) => {
        const items = byBucket[b.id];
        const total = items.reduce((s, o) => s + (o.montant_estime || 0), 0);
        return (
          <div key={b.id} className="rounded-lg bg-white border border-brand-line" data-testid={`simple-col-${b.id}`}>
            <div className="px-3 py-2 border-b border-brand-line">
              <div className="flex items-center justify-between">
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded border ${b.color}`}>{b.label}</span>
                <span className="text-[10px] text-brand-subtle font-mono">{items.length}</span>
              </div>
              <div className="text-[10px] text-brand-subtle font-mono mt-1">{fmtEur(total)}</div>
            </div>
            <div className="p-2 space-y-1.5 min-h-[150px] max-h-[70vh] overflow-y-auto">
              {items.length === 0
                ? <div className="text-[11px] text-brand-subtle text-center py-6">—</div>
                : items.map((o) => <Card key={o.id} opp={o} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FullBoard({ opps, dropTo }) {
  const byStage = opps.reduce((acc, o) => { (acc[o.stage] = acc[o.stage] || []).push(o); return acc; }, {});
  return (
    <div className="flex gap-3 overflow-x-auto pb-6" data-testid="kanban-board">
      {COMMERCIAL_STAGES.map((stage) => {
        const col = byStage[stage.id] || [];
        const total = col.reduce((s, o) => s + (o.montant_estime || 0), 0);
        return (
          <div
            key={stage.id}
            className="flex-shrink-0 w-[280px] rounded-lg bg-white border border-brand-line"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              const id = e.dataTransfer.getData("text/plain");
              if (id) dropTo(id, stage.id);
            }}
            data-testid={`col-${stage.id}`}
          >
            <div className="px-3 py-2 border-b border-brand-line flex items-center justify-between">
              <div>
                <div className="text-[11px] font-medium text-brand-ink">{stage.label}</div>
                <div className="text-[10px] text-brand-subtle font-mono mt-0.5">{col.length} · {fmtEur(total)}</div>
              </div>
            </div>
            <div className="p-2 space-y-2 min-h-[200px] max-h-[75vh] overflow-y-auto">
              {col.map((o) => <Card key={o.id} opp={o} draggable />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Card({ opp, draggable = false }) {
  return (
    <Link
      to={`/commercial/opportunites/${opp.id}`}
      draggable={draggable}
      onDragStart={draggable ? (e) => e.dataTransfer.setData("text/plain", opp.id) : undefined}
      className="block rounded-md border border-brand-line bg-brand-paper hover:border-brand-ink/25 p-2.5 cursor-grab active:cursor-grabbing"
      data-testid={`opp-card-${opp.id}`}
    >
      <div className="text-xs text-brand-ink font-medium truncate">{opp.entreprise_name || "—"}</div>
      {opp.contact_nom && <div className="text-[11px] text-brand-subtle truncate mt-0.5">{opp.contact_nom}</div>}
      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-brand-subtle">
        {opp.contact_telephone && <span className="inline-flex items-center gap-1 truncate"><Phone className="h-2.5 w-2.5" />{opp.contact_telephone}</span>}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[10px] text-brand-subtle truncate">{opp.teleprospectrice || "non assignée"}</span>
        <span className="text-[11px] font-mono text-brand-ink flex-shrink-0">{fmtEur(opp.montant_estime || opp.montant_final)}</span>
      </div>
    </Link>
  );
}
